<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      :aria-labelledby="titleId"
      :aria-describedby="descriptionId"
      aria-modal="true"
    >
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="close"></div>
      <div
        class="relative w-full max-w-md rounded-3xl bg-white dark:bg-slate-800 p-6 shadow-2xl transition-colors duration-300 focus:outline-none"
        :class="resolvedVariantClasses.container"
        tabindex="-1"
      >
        <div class="space-y-5">
          <div class="flex items-start justify-between gap-4">
            <div class="flex items-start gap-4">
              <div
                class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full"
                :class="resolvedVariantClasses.iconWrapper"
              >
                <component :is="iconComponent" class="h-6 w-6" :class="resolvedVariantClasses.icon" />
              </div>
              <div class="space-y-2">
                <h3 :id="titleId" class="text-lg font-bold text-gray-900 dark:text-white">
                  {{ resolvedTitle }}
                </h3>
                <p :id="descriptionId" class="text-sm text-gray-600 dark:text-slate-400">
                  {{ message }}
                </p>
              </div>
            </div>
            <button
              class="rounded-full border border-gray-200 dark:border-slate-600 p-2 text-gray-400 dark:text-slate-500 transition-colors hover:border-gray-300 dark:hover:border-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
              @click="close"
            >
              <IconX class="h-5 w-5" />
            </button>
          </div>
          <div class="flex flex-col gap-3 sm:flex-row">
            <button
              class="flex-1 inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors"
              :class="resolvedVariantClasses.action"
              @click="handleAction"
            >
              {{ resolvedActionLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, toRefs, watch } from "vue";

import IconAlertCircle from "@core/icons/IconAlertCircle.vue";
import IconCheck from "@core/icons/IconCheck.vue";
import IconX from "@core/icons/IconX.vue";

import { useTranslate } from "@common/composables/useTranslate";

const translate = useTranslate();

interface Props {
  isOpen: boolean;
  title?: string;
  message: string;
  actionLabel?: string;
  variant?: "error" | "success";
}

const props = defineProps<Props>();
const { isOpen, message } = toRefs(props);
const titleId = `error-modal-title-${Math.random().toString(36).slice(2)}`;
const descriptionId = `error-modal-description-${Math.random().toString(36).slice(2)}`;
const resolvedVariant = computed(() => props.variant ?? "error");
const iconComponent = computed(() => (resolvedVariant.value === "success" ? IconCheck : IconAlertCircle));
const resolvedVariantClasses = computed(() => {
  if (resolvedVariant.value === "success") {
    return {
      container: "border border-emerald-100 dark:border-emerald-900/40",
      iconWrapper: "bg-emerald-50 dark:bg-emerald-900/30",
      icon: "text-emerald-600 dark:text-emerald-400",
      action: "bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600"
    };
  }

  return {
    container: "border border-red-100 dark:border-red-900/40",
    iconWrapper: "bg-red-50 dark:bg-red-900/30",
    icon: "text-red-600 dark:text-red-400",
    action: "bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600"
  };
});
const resolvedTitle = computed(() => {
  const key = resolvedVariant.value === "success" ? "common.successTitle" : "common.errorTitle";
  return props.title ?? translate(key);
});
const resolvedActionLabel = computed(() => props.actionLabel ?? translate("common.dismissButton"));

const emit = defineEmits<{
  close: [];
  confirm: [];
}>();

const close = () => {
  emit("close");
};

const handleAction = () => {
  emit("confirm");
  close();
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape" && isOpen.value) {
    close();
  }
};

onMounted(() => {
  window.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeydown);
});

watch(isOpen, value => {
  if (!value || typeof document === "undefined") {
    return;
  }
  queueMicrotask(() => {
    const dialog = document.querySelector<HTMLElement>(`#${titleId}`)?.closest<HTMLElement>("[role='dialog']");
    dialog?.focus();
  });
});
</script>
